import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useActiveContextReady } from './useActiveContextReady';
import { config } from '../config';
import type { ConfigChange } from './useConfigChanges';
import type { Credential } from './useCredentials';

export interface VariableOption {
  value: string;
  label: string;
}

/** One VLAN range in a `vlan_range_list` value; `start === end` is a single VLAN. */
export interface VlanRange {
  start: number;
  end: number;
}

/** A `credential` variable's value: the credential, by id. Its secret stays on the gateways. */
export interface CredentialValue {
  credentialId: string;
}

/**
 * A typed operation-variable value. Mirrors the backend's untagged `VarValue`:
 * a scalar is a bare string, a VLAN-range list is an array of {start,end}, a
 * credential is `{ credentialId }`.
 * New typed kinds are added here as union members, not as parallel fields.
 */
export type VarValue = string | VlanRange[] | CredentialValue;

/** Whether `value` is a chosen credential. */
export function isCredentialValue(value: VarValue | undefined): value is CredentialValue {
  return typeof value === 'object' && !Array.isArray(value) && typeof value.credentialId === 'string';
}

export interface OperationVariable {
  name: string;
  varType: string;
  /** The one family an `ip_address` or `prefix` variable accepts; absent, either. */
  family?: 'ipv4' | 'ipv6' | null;
  min: number | null;
  max: number | null;
  possibleValues: VariableOption[] | null;
  multiSelect: boolean;
  allowCustom: boolean;
  /** A `credential` variable's accepted credential types. */
  credentialTypes?: Credential['type'][] | null;
  /** Where a `credential` variable's credential comes from: the device's region and division. */
  credentialScope?: { regionId: string; divisionId: string | null } | null;
  /** What the variable is when it is left out, for one that may be: the backend fills it in. */
  default?: string | null;
}

/** Whether `value` leaves out a variable that may be: the backend gives it its default. */
export function isLeftOut(variable: OperationVariable, value: VarValue | undefined): boolean {
  return variable.default != null && (value === undefined || (typeof value === 'string' && value.trim() === ''));
}

/** `values` without the ones left out, for the backend to fill in. */
export function withoutLeftOut(
  variables: OperationVariable[],
  values: Record<string, VarValue>,
): Record<string, VarValue> {
  const leftOut = new Set(variables.filter((v) => isLeftOut(v, values[v.name])).map((v) => v.name));
  return Object.fromEntries(Object.entries(values).filter(([name]) => !leftOut.has(name)));
}

export interface AvailableOperation {
  operationName: string;
  /** Profile-declared display name; UI falls back to a formatted operationName. */
  title: string | null;
  description: string | null;
  variables: OperationVariable[];
  /**
   * The controller that performs it, by name, when the device's owning
   * controller does and the device itself is never contacted. Null (or
   * absent, from an older backend) when the device performs it.
   */
  via?: string | null;
  /**
   * What it changes: the device, or a setting of the device's controller site
   * and so every device on that site. Absent from an older backend: the device.
   */
  scope?: 'device' | 'site';
  /** For a `site` operation, the site and every device it reaches. */
  site?: ControllerSiteReach | null;
}

/** A controller site, as a site-scoped operation reaches it. */
export interface ControllerSiteReach {
  /** The id the controller's API addresses the site by (UniFi: `default`). */
  id: string;
  /** The name the controller shows, when it gave one. */
  name: string | null;
  /** The controller integration holding the site, by name. */
  controller: string;
  /** Every device on the site, the one asked about included. */
  devices: { id: string; name: string }[];
}

interface AvailableOperationsResponse {
  operations: AvailableOperation[];
}

export function useAvailableOperations(deviceId: string | undefined) {
  const contextReady = useActiveContextReady();
  return useQuery<AvailableOperation[]>({
    queryKey: ['availableOperations', deviceId],
    queryFn: async () => {
      if (!deviceId) throw new Error('Device ID is required');
      const response = await axios.get<AvailableOperationsResponse>(
        `${config.backendApi}/api/devices/${deviceId}/available-operations`,
        { withCredentials: true }
      );
      return response.data.operations;
    },
    enabled: !!deviceId && contextReady,
  });
}

interface ApplyConfigChangeParams {
  operations: Array<{
    operationName: string;
    variables: Record<string, VarValue>;
  }>;
}

export function useApplyConfigChange(deviceId: string) {
  const queryClient = useQueryClient();
  return useMutation<ConfigChange, Error, ApplyConfigChangeParams>({
    mutationFn: async (params) => {
      const response = await axios.post<ConfigChange>(
        `${config.backendApi}/api/devices/${deviceId}/config-changes`,
        params,
        { withCredentials: true }
      );
      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['configChanges', deviceId] });
      void queryClient.invalidateQueries({ queryKey: ['configSnapshots', deviceId] });
    },
  });
}
